import { gql, useMutation } from "@apollo/client";
import { alert } from "@components/common/Alert";
import { Input } from "@components/common/inputs/Input";
import { ConfirmModal } from "@components/common/modals/PopupModals";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "@hooks";
import { TextareaAutosize } from "@mui/base";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import TeamClinic from "../../../assets/imgs/team-clinic.png";
import { BasicButton } from "../../common/buttons/BasicButton";
import TeamModal from "../TeamModal";

const REQUEST_TEAM_SETUP = gql`
  mutation requestTeamSetup(
    $email: String!
    $customMessage: String!
  ) {
    requestTeamSetup(email: $email, customMessage: $customMessage) {
      ok
    }
  }
`;

export function EmptyState({ orgUuid, refetch, currentUser }) {
  const [requestTeamSetup] = useMutation(REQUEST_TEAM_SETUP);
  const [searchParams, setSearchParams] = useSearchParams();
  const createTeam = searchParams.has("createTeam");
  const [shouldShowCreateTeamModal, setShouldShowCreateTeamModal] =
    useState(createTeam);
  const [
    shouldShowInviteManagerModal,
    setShouldShowInviteManagerModal,
  ] = useState(false);
  const { accountType } = useAuth();
  const { control, handleSubmit } = useForm();

  const onSubmit = async ({ email, customMessage }) => {
    try {
      await requestTeamSetup({
        variables: {
          email,
          customMessage,
        },
      });
      alert("success", `Invite sent to ${email}!`);
    } catch (error) {
      alert("error", `Failed to send invite: ${error.message}`);
    }
    setShouldShowInviteManagerModal(false);
  };

  const hideCreateTeamModal = () => {
    const currentParams = searchParams;
    currentParams.delete("createTeam");
    setSearchParams(currentParams);
    setShouldShowCreateTeamModal(false);
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <TeamModal
        isOpen={shouldShowCreateTeamModal}
        onClose={hideCreateTeamModal}
        orgIsManualBilling={false}
        hasBillingSetup={accountType === "unlimited"}
        orgUuid={orgUuid}
        isCreatingTeam={true}
        refetch={refetch}
      />
      <ConfirmModal
        shouldShow={shouldShowInviteManagerModal}
        hideModal={() => setShouldShowInviteManagerModal(false)}
        title="Invite Your Practice Manager"
        cancelText="Cancel"
        confirmText="Send Invite"
        cancelAction={() => setShouldShowInviteManagerModal(false)}
        confirmAction={handleSubmit(onSubmit)}
        subtitle="Send a message to your practice manager so they can create a team for you."
      >
        <div>
          <form className="space-y-4">
            <div>
              <label className="my-2 font-semibold">
                Practice Manager's Email
              </label>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <Input
                    placeholder="Enter your practice manager's email"
                    {...field}
                  />
                )}
              />
            </div>
            <div>
              <label className="my-2 font-semibold">
                Custom Message
              </label>
              <Controller
                control={control}
                name="customMessage"
                defaultValue={
                  "Hi! I'm inviting you to create a team for our clinic on Scribenote. This is so we can share notes and templates among our care team and have centralized account management."
                }
                render={({ field }) => (
                  <TextareaAutosize
                    minRows={3}
                    className="text-sm w-full appearance-none resize-none bg-gray-100 dark:bg-gray-700/50 rounded-xl p-3"
                    {...field}
                  />
                )}
              />
            </div>
          </form>
        </div>
      </ConfirmModal>
      <div className="flex flex-col items-center space-y-10 p-10 rounded-xl">
        <div className="flex flex-col text-center space-y-2">
          <h2>Welcome to Teams Mode!</h2>
          <a
            className="dark:text-indigo-300 text-indigo-500"
            target="_blank"
            rel="noreferrer"
            href="https://docs.scribenote.com/en/articles/8618740-teams-mode"
          >
            <FontAwesomeIcon icon={faQuestionCircle} /> What's This?
          </a>
        </div>

        <img
          src={TeamClinic}
          alt="Teams Mode Empty State"
          className="w-1/2"
        />
        <h5 className="max-w-xl text-center">
          Teams Mode allows you to share notes and templates with
          fellow clinic staff. Let's get started!
        </h5>
        <div className="flex flex-row space-x-2">
          {currentUser?.user?.role?.id !== "2" && (
            <BasicButton
              importance="primary"
              size="lg"
              onClick={() => setShouldShowInviteManagerModal(true)}
            >
              I want my practice manager to set up a team
            </BasicButton>
          )}
          <BasicButton
            importance={
              currentUser?.user?.role?.id === "2"
                ? "primary"
                : "secondary"
            }
            size="lg"
            onClick={() => setShouldShowCreateTeamModal(true)}
            data-testid={
              currentUser?.user?.role?.id === "2"
                ? "create-team-button"
                : "setup-team-button"
            }
          >
            {currentUser?.user?.role?.id === "2"
              ? "+ Create a Team"
              : "I'll set one up"}
          </BasicButton>
        </div>
      </div>
    </div>
  );
}
